"use client";

import { useEffect, useRef, useState } from "react";

// Verifone Checkout inline payment form — shared by the booking checkout and the
// gift-voucher checkout so the payment step looks and behaves identically.
//
// Injects the loader <script> as a child of the container div; with no containerId
// param the SDK mounts the hosted card iframe at the script's parentElement (this
// div). A spinner shows until the iframe's first iFrameSizer message arrives. The
// session is created WITHOUT a return_url, so a declined card stays on the form for
// retry and the parent page detects the outcome by polling its status endpoint.
export function VerifonePaymentFrame({
  iframeUrl,
  onScriptError,
}: {
  iframeUrl: string;
  onScriptError?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  // Keep the latest onScriptError without putting it in the mount effect's deps —
  // it's an inline arrow from the parent (new ref every render), and including it
  // would re-run the effect on every parent render, tearing down + re-injecting the
  // loader script. That race makes loader.js fail with "Invalid jsclient container".
  const onErrRef = useRef(onScriptError);
  useEffect(() => { onErrRef.current = onScriptError; }, [onScriptError]);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    setReady(false);
    container.innerHTML = ""; // clear any previous attempt
    const script = document.createElement("script");
    script.src = iframeUrl; // no containerId → SDK mounts at script.parentElement
    script.async = true;
    script.onerror = () => onErrRef.current?.();
    container.appendChild(script);

    const onMsg = (e: MessageEvent) => {
      if (typeof e.data === "string" && e.data.startsWith("[iFrameSizer]")) {
        setReady(true);
        window.removeEventListener("message", onMsg);
      }
    };
    window.addEventListener("message", onMsg);
    return () => {
      container.innerHTML = "";
      window.removeEventListener("message", onMsg);
    };
  }, [iframeUrl]); // only re-mount when the session URL changes

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {!ready && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-cream-warm">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-ink/20 border-t-ink" />
          <p className="text-[13px] font-light text-ink-soft">Loading payment form…</p>
        </div>
      )}
      <div ref={ref} className="min-h-[400px]" />
    </div>
  );
}
